// Recognized signatures of a transient dynamic-import/chunk-load failure (network blip, CDN edge
// hiccup, ad-blocker) as opposed to a genuine application error. Kept in one place because both the
// Sentry ignore list and the recovery logic below need to agree on what counts as this error class.
const DynamicImportErrorPatterns: Array<string | RegExp> = ["Failed to fetch dynamically imported module", "Importing a module script failed", /Loading chunk \S+ failed/i, "ChunkLoadError"];

const StorageKeyPrefix = "dynamicImportRetry:";

export const getDynamicImportErrorKey = (error: unknown): string => (error instanceof Error ? error.message : String(error));

export const isDynamicImportError = (error: unknown): boolean => {
  const message = getDynamicImportErrorKey(error);
  return DynamicImportErrorPatterns.some((pattern) => (typeof pattern === "string" ? message.includes(pattern) : pattern.test(message)));
};

export { DynamicImportErrorPatterns };

/**
 * Loop protection shared across recovery attempts within the same tab session: a chunk that keeps
 * failing (e.g. genuinely removed or broken) must eventually stop retrying instead of looping forever
 * across component remounts or repeated navigations.
 */
export const canRetryDynamicImport = (error: unknown, maxAttempts: number): boolean => {
  return getAttemptCount(error) < maxAttempts;
};

export const recordDynamicImportAttempt = (error: unknown): void => {
  const key = storageKey(error);
  sessionStorage.setItem(key, String(getAttemptCount(error) + 1));
};

const getAttemptCount = (error: unknown): number => {
  const raw = sessionStorage.getItem(storageKey(error));
  return raw ? parseInt(raw, 10) : 0;
};

const storageKey = (error: unknown): string => StorageKeyPrefix + getDynamicImportErrorKey(error);
